import { useEffect, useRef, useState } from 'react';

import { useMergedRef } from '@/hook';

import { useBaseInput } from '../VBaseInput';
import { BASE_INPUT_SIZE } from '../VBaseInput/variable';
import { VFlex } from '../VFlex';
import { Cancel, Search, Visibility, VisibilityOff } from '../asset';
import { new__cn as cn } from '../util';
import twStyle from './style';
import type { VInputIcon, VInputProps, VInputType } from './type';

const VInnerInput = <T extends VInputType>({
  ref,
  ...props
}: VInputProps<T> &
  Omit<React.ComponentPropsWithRef<'input'>, 'size' | 'type'>) => {
  const {
    inputClassName,
    inputStyle,
    size = BASE_INPUT_SIZE.LG,
    step,
    type = 'text' as T,
    onBlur,
    onFocus,
    ...rest
  } = props;
  const { id, status, setStatus } = useBaseInput();
  const [innerType, setInnerType] = useState(type);
  const inputRef = useRef<HTMLInputElement>(null);
  const mergedRef = useMergedRef(inputRef, ref);
  const disabled = status.disabled ?? false;
  const renderRightSide = () => {
    if (type === 'password' || type === 'search') {
      const IconElement =
        type === 'search'
          ? Search
          : type === 'password'
            ? innerType === 'password'
              ? VisibilityOff
              : Visibility
            : null;

      if (!IconElement) return;

      const iconProps: Omit<VInputIcon, 'Element'> = {
        className: style.inputIcon,
      };

      return (
        <IconElement
          {...iconProps}
          role="button"
          onClick={event => {
            if (type === 'password') {
              setInnerType((innerType === 'text' ? 'password' : 'text') as T);
            } else {
              Object.defineProperties(event, {
                type: { value: 'change' },
                target: { value: inputRef.current },
                currentTarget: { value: inputRef.current },
              });

              rest.onChange?.(
                event as unknown as React.ChangeEvent<HTMLInputElement>,
              );
            }
          }}
        />
      );
    }

    return (props as any).renderRightSide?.();
  };

  const style = twStyle({ ...props, size });

  useEffect(() => {
    setInnerType(type);
  }, [type]);

  return (
    <>
      <VFlex className="flex-1" justify="between" items="center">
        <input
          ref={mergedRef}
          id={id}
          className={cn(style.input, inputClassName)}
          style={inputStyle}
          type={innerType}
          onBlur={e => {
            setStatus(prev => ({ ...prev, focused: false }));
            onBlur?.(e);
          }}
          onFocus={e => {
            setStatus(prev => ({ ...prev, focused: true }));
            onFocus?.(e);
          }}
          data-autofocus={rest.autoFocus ? 'true' : undefined}
          disabled={disabled}
          step={step}
          {...rest}
        />
      </VFlex>
      <VFlex className={style.inputRightSide} items="center" gap={0.25}>
        {type === 'search' && rest.value && (
          <Cancel
            role="button"
            className={cn(style.inputIcon, 'text-gray-400')}
            onClick={event => {
              if (inputRef.current) {
                inputRef.current.value = '';

                Object.defineProperties(event, {
                  type: { value: 'change' },
                  target: { value: inputRef.current },
                  currentTarget: { value: inputRef.current },
                });

                rest.onChange?.(
                  event as unknown as React.ChangeEvent<HTMLInputElement>,
                );
              }
            }}
          />
        )}
        {renderRightSide()}
      </VFlex>
    </>
  );
};

export default VInnerInput;
